import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 px-4 text-center">
          <div className="max-w-lg mx-auto">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要阅读的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium px-6 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const related = articles
    .filter((a) => a.id !== article.id && (a.category === article.category || a.tags.some((t) => article.tags.includes(t))))
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: 'zh-CN',
    keywords: article.tags.join(','),
  };

  const categoryColors: Record<string, string> = {
    '剧情攻略': 'bg-primary-100 text-primary-700',
    '角色解析': 'bg-accent-100 text-accent-700',
    '主题评测': 'bg-secondary-100 text-secondary-700',
    '感动结局': 'bg-primary-100 text-primary-800',
    '玩家评价': 'bg-secondary-100 text-secondary-800',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <ScrollToTop />
      <main>
        {/* Header */}
        <div className="bg-foreground-950 pt-24 pb-10 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category?tab=${article.category}` },
                { label: article.title },
              ]}
            />
            <span className={`inline-block mt-4 text-xs font-medium px-2.5 py-0.5 rounded-full ${categoryColors[article.category] || 'bg-background-200 text-foreground-500'}`}>
              {article.category}
            </span>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 mt-3 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </div>
        </div>

        {/* Article Body */}
        <div className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10">
              {/* Main Content */}
              <article className="flex-1 min-w-0">
                {/* Featured Image */}
                <div className="w-full h-72 md:h-96 rounded-lg overflow-hidden mb-8">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* Content */}
                <div
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Tags */}
                <div className="mt-10 pt-8 border-t border-background-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground-500 mr-1">标签：</span>
                    {article.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/category?tab=${article.category}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors"
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Share */}
                <div className="mt-6 pt-6 border-t border-background-200 flex items-center gap-3">
                  <span className="text-sm text-foreground-500">分享：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-fill text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-fill text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-sm"></i>
                  </button>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="lg:w-72 flex-shrink-0">
                <div className="sticky top-24">
                  {/* Author Card */}
                  <div className="bg-background-50 border border-background-200 rounded-lg p-5 mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-user-3-line"></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{article.author}</div>
                        <div className="text-xs text-foreground-500">缘之空深度内容团队</div>
                      </div>
                    </div>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      悠之空编辑部致力于为缘之空爱好者提供最全面、最深度的内容解读与分析。
                    </p>
                  </div>

                  {/* Table of Contents */}
                  {related.length > 0 && (
                    <div className="bg-background-50 border border-background-200 rounded-lg p-5">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                        <i className="ri-article-line text-primary-500"></i>
                        相关文章
                      </h3>
                      <div className="flex flex-col gap-3">
                        {related.map((rel) => (
                          <Link
                            key={rel.id}
                            to={`/article/${rel.id}`}
                            className="group cursor-pointer"
                          >
                            <div className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                              {rel.title}
                            </div>
                            <div className="text-xs text-foreground-400 mt-1">{rel.readTime}</div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </div>

        {/* Related Articles Grid */}
        {related.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop="">
                {related.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTopButton />
    </>
  );
}